import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { AssetCatalogEntry, AssetVisibilityContext, BuxusOperatorPayload } from '@buxus/shared'
import { buildAssetCatalogEntries } from '@buxus/shared'
import { Map as MapView } from '../../Map'
import type { PipelineMapOperatorDirectoryEntry } from '../../mapPipelinePopupDom'
import { useMapRailHoverPreview } from '../../platform/ui/useMapRailHoverPreview'
import { useMobileWebViewport } from '../../platform/ui/mobileViewport'
import { AssetMapPreviewCard } from './AssetMapPreviewCard'
import { flyMapToAsset } from './flyMapToAsset'
import { isMobileWebViewport } from '../../platform/ui/mobileViewport'
import type { ScreenInteractionContext } from '../../platform/analytics/interactionAnalytics'
import { MapAssetOverlayRail } from './MapAssetOverlayRail'

type AssetsMapRailOverlayProps = {
  payload: BuxusOperatorPayload
  resolveContext: AssetVisibilityContext
  stakeholderCode: string | null
  jurisdictionCountyStateKeys: ReadonlySet<string> | null
  pipelineOperatorDirectory?: readonly PipelineMapOperatorDirectoryEntry[]
  onViewPipelineOperator?: (operatorId: string) => void
  /** Rail click: sidebar asset context only (stay on current map screen). */
  onFocusAssetInSidebar: (assetId: string) => void
  /** Persisted sidebar/tree asset — rail highlight follows this. */
  contextAssetId?: string | null
  mapKey?: string
  hostClassName?: string
  analytics?: ScreenInteractionContext
}

/** Map + asset rail + hover preview (All Assets map mode, Operator Overview). */
export function AssetsMapRailOverlay({
  payload,
  resolveContext,
  stakeholderCode,
  jurisdictionCountyStateKeys,
  pipelineOperatorDirectory,
  onViewPipelineOperator,
  onFocusAssetInSidebar,
  contextAssetId = null,
  mapKey = 'assets-map-rail',
  hostClassName = 'all-assets-map-host',
  analytics,
}: AssetsMapRailOverlayProps) {
  const isMobileWeb = useMobileWebViewport()
  const [railOpen, setRailOpen] = useState(() => !isMobileWebViewport())
  const [pipelineOverlayActive, setPipelineOverlayActive] = useState(false)
  /** Transient map pan target; cleared on background click (sidebar context persists). */
  const [mapFocusAssetId, setMapFocusAssetId] = useState<string | null>(null)
  const mapInstanceRef = useRef<import('mapbox-gl').Map | null>(null)
  /** Set synchronously on rail click so Map skips operator flyTo before React re-renders. */
  const operatorFramingLockRef = useRef(false)

  const {
    hoverId,
    previewId,
    showPreview,
    onHoverStart,
    onHoverEnd,
    onPreviewCardEnter,
    onPreviewCardLeave,
    clearPreview,
    armFocusHover,
  } = useMapRailHoverPreview({ overlayBlocked: pipelineOverlayActive })

  const catalog = useMemo(
    () => buildAssetCatalogEntries(payload, resolveContext),
    [payload, resolveContext],
  )

  const catalogById = useMemo(() => new Map(catalog.map((e) => [e.id, e])), [catalog])

  const activePreviewEntry = previewId ? (catalogById.get(previewId) ?? null) : null

  const resetMapFocus = useCallback(() => {
    operatorFramingLockRef.current = false
    clearPreview()
    setMapFocusAssetId(null)
  }, [clearPreview])

  const onFocusAsset = useCallback(
    (entry: AssetCatalogEntry) => {
      operatorFramingLockRef.current = true
      const map = mapInstanceRef.current
      if (map && entry.hasCoordinates && entry.center) {
        flyMapToAsset(map, entry.center, entry.zoom ?? 8)
      }
      armFocusHover(entry.id)
      setMapFocusAssetId(entry.id)
      onFocusAssetInSidebar(entry.id)
    },
    [armFocusHover, onFocusAssetInSidebar],
  )

  const onRailOpenChange = useCallback(
    (open: boolean) => {
      setRailOpen(open)
      if (!open) resetMapFocus()
    },
    [resetMapFocus],
  )

  const onMapInstance = useCallback((map: import('mapbox-gl').Map | null) => {
    mapInstanceRef.current = map
  }, [])

  useEffect(() => {
    const onKeyDown = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') resetMapFocus()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [resetMapFocus])

  return (
    <div
      className={hostClassName}
      data-map-rail-open={railOpen ? 'true' : undefined}
    >
      <MapView
        key={mapKey}
        payload={payload}
        stakeholderCode={stakeholderCode}
        embedded
        jurisdictionCountyStateKeys={jurisdictionCountyStateKeys}
        pipelineOperatorDirectory={pipelineOperatorDirectory}
        onViewPipelineOperator={onViewPipelineOperator}
        suppressOperatorFraming={mapFocusAssetId != null}
        operatorFramingLockRef={operatorFramingLockRef}
        onMapInstance={onMapInstance}
        onMapBackgroundClick={resetMapFocus}
        onPipelineOverlayActiveChange={setPipelineOverlayActive}
        analytics={analytics}
      />
      <MapAssetOverlayRail
        entries={catalog}
        totalCount={catalog.length}
        open={railOpen}
        selectedAssetId={contextAssetId}
        hoverAssetId={hoverId}
        onOpenChange={onRailOpenChange}
        onPreviewHoverStart={onHoverStart}
        onPreviewHoverEnd={onHoverEnd}
        onFocusAsset={onFocusAsset}
        analytics={analytics}
      />
      {activePreviewEntry && showPreview && !isMobileWeb ? (
        <AssetMapPreviewCard
          entry={activePreviewEntry}
          onMouseEnter={onPreviewCardEnter}
          onMouseLeave={onPreviewCardLeave}
        />
      ) : null}
    </div>
  )
}
